import React, { FC, useEffect, useRef, useState } from 'react'

import { cx, css } from '@linaria/core'
import { styled } from '@linaria/react'
import { FormattedMessage } from 'react-intl'

import { VoidHandler } from 'common/types'
import { FooterWaveAboutLessSvg } from 'components/designSystem/svgr/FooterWaveAboutLessSvg'
import { FooterWaveAboutMoreSvg } from 'components/designSystem/svgr/FooterWaveAboutMoreSvg'
import { TextWaveUnderline } from 'components/designSystem/TextWaveUnderline/TextWaveUnderline'
import { More } from 'components/designSystem/Typography/More/More'
import { Typography } from 'components/designSystem/Typography/Typography'

/**
 * 24 высота строки * 3 строки
 */
const heightClamp = 24 * 3

export const About: FC<{
  text: string
}> = ({ text }) => {
  const textRef = useRef<HTMLDivElement>(null)
  const [isClamped, setIsClamped] = useState<boolean>(false)

  const handleClick = () => {
    setIsClamped(false)
  }

  useEffect(() => {
    const checkClamp = () => {
      if (textRef.current) {
        const { clientHeight } = textRef.current
        setIsClamped(clientHeight > heightClamp)
      }
    }

    checkClamp()
  }, [text])

  return (
    <Parent>
      <Header
        title={
          <FormattedMessage
            id="app.profile.about.title"
            defaultMessage="О себе"
          />
        }
        fontSize={16}
        variant="with-quotes"
        data-name="greeting-title-section"
      />
      <Body fontSize={20} tag="p">
        <Clamp
          className={cx(isClamped && clampedCss)}
          data-name="profile-greeting"
          ref={textRef}
        >
          {text}
        </Clamp>
      </Body>
      <div>
        {isClamped && (
          <MoreAbout onClick={handleClick} data-name="profile-greeting-more" />
        )}
        {!isClamped && <LessAbout />}
      </div>
    </Parent>
  )
}

const MoreAbout: FC<{ onClick: VoidHandler }> = ({ onClick }) => {
  return (
    <FooterParent>
      <WaveLeft className={cornerCss} />
      <WaveCenter>
        <FooterWaveAboutMoreSvg />
      </WaveCenter>
      <PositionMore>
        <More onClick={onClick} />
      </PositionMore>
      <WaveRight className={cornerCss} />
    </FooterParent>
  )
}

const LessAbout = () => {
  return (
    <FooterParent>
      <WaveLeft className={cornerCss} />
      <WaveCenter>
        <FooterWaveAboutLessSvg />
      </WaveCenter>
      <WaveRight className={cornerCss} />
    </FooterParent>
  )
}

const Parent = styled.div`
  display: grid;
  grid-template-rows: 1fr;
  justify-content: center;
`

const Header = styled(TextWaveUnderline)`
  display: grid;
  grid-template-rows: 1fr auto;
  grid-template-columns: 1fr auto;
  align-items: center;
  margin: 0;
  color: var(--foreground-soft);
  border-radius: var(--spacing-32px) var(--spacing-32px) 0 0;
  background-color: var(--pearl-opacity-default);
  padding: var(--spacing-16px) var(--spacing-20px) 0 var(--spacing-20px);
`

const Body = styled(Typography)`
  display: flex;
  min-width: 0;
  overflow-wrap: anywhere;
  background-color: var(--pearl-opacity-default);
  padding: var(--spacing-16px) var(--spacing-20px);
  margin: 0;
`

const Clamp = styled.span`
  display: -webkit-box;
`

const clampedCss = css`
  -webkit-line-clamp: 3;
  -webkit-box-orient: vertical;
  overflow: hidden;
`

const cornerCss = css`
  width: 32px;
  background-color: var(--pearl-opacity-default);
`

const WaveLeft = styled.div`
  html[dir='ltr'] & {
    border-bottom-left-radius: 32px;
  }

  html[dir='rtl'] & {
    border-bottom-right-radius: 32px;
  }
`
const WaveRight = styled.div`
  html[dir='ltr'] & {
    border-bottom-right-radius: 32px;
  }

  html[dir='rtl'] & {
    border-bottom-left-radius: 32px;
  }
`

const WaveCenter = styled.div`
  display: flex;
  align-items: flex-end;
  justify-content: center;
`

const FooterParent = styled.div`
  position: relative;
  display: grid;
  grid-template-columns: 32px 1fr 32px;
  justify-content: center;
`

const PositionMore = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  position: absolute;
  bottom: -5px;
  left: 50%;
  transform: translateX(-50%);
  height: 100%;
`
